import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  CalendarIcon,
  SpeakerWaveIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useUser } from '../contexts/UserContext';
import { useToast } from '../contexts/ToastContext';
import { progressApi, ProgressData } from '../services/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface ProgressTrackingProps {
  className?: string;
}

const ProgressTracking: React.FC<ProgressTrackingProps> = ({ className = '' }) => {
  const { user } = useUser();
  const { showError } = useToast();
  const [progressData, setProgressData] = useState<ProgressData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAllCharts, setShowAllCharts] = useState(true);

  useEffect(() => {
    const loadProgressData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const data = await progressApi.getMyProgress();
        setProgressData(data);
      } catch (error) {
        console.error('Error loading progress data:', error);
        showError('Load Failed', 'Failed to load your progress data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadProgressData();
  }, [user, showError]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatFrequency = (freq: number) => {
    if (freq >= 1000) {
      return `${(freq / 1000).toFixed(1)} kHz`;
    }
    return `${freq} Hz`;
  };

  const formatVolume = (vol: number) => {
    return `${Math.round(20 * Math.log10(vol * 100))} dB`;
  };

  const getTHISeverity = (score: number) => {
    if (score <= 16) return { level: 'Slight', color: '#10B981' };
    if (score <= 36) return { level: 'Mild', color: '#F59E0B' };
    if (score <= 56) return { level: 'Moderate', color: '#F97316' };
    if (score <= 76) return { level: 'Severe', color: '#EF4444' };
    return { level: 'Catastrophic', color: '#7F1D1D' };
  };

  const getChartData = (metric: 'thi' | 'frequency' | 'volume') => {
    if (!progressData) {
      return {
        datasets: []
      };
    }

    const thiData = progressData.questionnaire_results.map(result => ({
      x: formatDate(result.date),
      y: result.total_score
    }));

    const frequencyData = progressData.tinnitus_profiles.map(profile => ({
      x: formatDate(profile.timestamp),
      y: profile.frequency_hz
    }));

    const volumeData = progressData.tinnitus_profiles.map(profile => ({
      x: formatDate(profile.timestamp),
      y: Math.round(20 * Math.log10(profile.volume_normalized * 100))
    }));

    const datasets = [];

    if (metric === 'thi' && thiData.length > 0) {
      datasets.push({
        label: 'THI Score',
        data: thiData,
        borderColor: '#6FB1A0',
        backgroundColor: '#6FB1A0',
        tension: 0.1,
        yAxisID: 'y',
      });
    }

    if (metric === 'frequency' && frequencyData.length > 0) {
      datasets.push({
        label: 'Frequency (Hz)',
        data: frequencyData,
        borderColor: '#AEC6CF',
        backgroundColor: '#AEC6CF',
        tension: 0.1,
        yAxisID: 'y',
      });
    }

    if (metric === 'volume' && volumeData.length > 0) {
      datasets.push({
        label: 'Volume (dB)',
        data: volumeData,
        borderColor: '#F5A623',
        backgroundColor: '#F5A623',
        tension: 0.1,
        yAxisID: 'y',
      });
    }

    return {
      datasets
    };
  };

  const getChartOptions = (metric: 'thi' | 'frequency' | 'volume') => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: metric === 'thi' 
            ? 'Tinnitus Handicap Inventory Score Over Time'
            : metric === 'frequency'
            ? 'Tinnitus Frequency Over Time'
            : 'Tinnitus Volume Over Time',
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Date'
          }
        },
        y: {
          title: {
            display: true,
            text: metric === 'thi' 
              ? 'THI Score'
              : metric === 'frequency'
              ? 'Frequency (Hz)'
              : 'Volume (dB)'
          }
        }
      },
    };

    if (metric === 'thi') {
      return {
        ...baseOptions,
        scales: {
          ...baseOptions.scales,
          y: {
            ...baseOptions.scales.y,
            beginAtZero: true,
            max: 100,
          }
        }
      };
    }

    return baseOptions;
  };

  const getLatestValues = () => {
    if (!progressData) return null;

    const latestTHI = progressData.questionnaire_results[progressData.questionnaire_results.length - 1];
    const latestProfile = progressData.tinnitus_profiles[progressData.tinnitus_profiles.length - 1];

    return {
      thi: latestTHI,
      profile: latestProfile
    };
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="text-center py-8">
          <ChartBarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Sign In Required</h3>
          <p className="text-gray-600">Please sign in to view your progress tracking.</p>
        </div>
      </div>
    );
  }

  if (!progressData || (progressData.questionnaire_results.length === 0 && progressData.tinnitus_profiles.length === 0)) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="text-center py-8">
          <ChartBarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Yet</h3>
          <p className="text-gray-600">Complete assessments and questionnaires to start tracking your progress.</p>
        </div>
      </div>
    );
  }

  const latestValues = getLatestValues();

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-auris-dark flex items-center">
          <ChartBarIcon className="w-5 h-5 mr-2" />
          Progress Tracking
        </h2>
        
        <div className="flex space-x-2">
          <button
            onClick={() => setShowAllCharts(!showAllCharts)}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              showAllCharts
                ? 'bg-auris-teal text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {showAllCharts ? 'All Charts' : 'Single Chart'}
          </button>
        </div>
      </div>

      {showAllCharts ? (
        <div className="space-y-8">
          {/* THI Chart */}
          {progressData?.questionnaire_results && progressData.questionnaire_results.length > 0 && (
            <div>
              <div className="h-64 mb-4">
                <Line data={getChartData('thi')} options={getChartOptions('thi')} />
              </div>
            </div>
          )}

          {/* Frequency Chart */}
          {progressData?.tinnitus_profiles && progressData.tinnitus_profiles.length > 0 && (
            <div>
              <div className="h-64 mb-4">
                <Line data={getChartData('frequency')} options={getChartOptions('frequency')} />
              </div>
            </div>
          )}

          {/* Volume Chart */}
          {progressData?.tinnitus_profiles && progressData.tinnitus_profiles.length > 0 && (
            <div>
              <div className="h-64 mb-4">
                <Line data={getChartData('volume')} options={getChartOptions('volume')} />
              </div>
            </div>
          )}

          {/* Latest Values Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {latestValues?.thi && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-600">Latest THI Score</div>
                    <div className="text-2xl font-bold text-auris-teal">
                      {latestValues.thi.total_score}
                    </div>
                  </div>
                  <ClipboardDocumentListIcon className="w-8 h-8 text-auris-teal" />
                </div>
                <div className="mt-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    getTHISeverity(latestValues.thi.total_score).color === '#10B981' ? 'bg-green-100 text-green-800' :
                    getTHISeverity(latestValues.thi.total_score).color === '#F59E0B' ? 'bg-yellow-100 text-yellow-800' :
                    getTHISeverity(latestValues.thi.total_score).color === '#F97316' ? 'bg-orange-100 text-orange-800' :
                    getTHISeverity(latestValues.thi.total_score).color === '#EF4444' ? 'bg-red-100 text-red-800' :
                    'bg-red-200 text-red-900'
                  }`}>
                    {getTHISeverity(latestValues.thi.total_score).level}
                  </span>
                </div>
              </div>
            )}

            {latestValues?.profile && (
              <>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-600">Latest Frequency</div>
                      <div className="text-2xl font-bold text-auris-blue">
                        {formatFrequency(latestValues.profile.frequency_hz)}
                      </div>
                    </div>
                    <SpeakerWaveIcon className="w-8 h-8 text-auris-blue" />
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-600">Latest Volume</div>
                      <div className="text-2xl font-bold text-auris-teal">
                        {formatVolume(latestValues.profile.volume_normalized)}
                      </div>
                    </div>
                    <SpeakerWaveIcon className="w-8 h-8 text-auris-teal" />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Data Summary */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div className="flex items-center">
              <CalendarIcon className="w-4 h-4 mr-2" />
              <span>
                {progressData?.questionnaire_results.length || 0} THI assessments, {progressData?.tinnitus_profiles.length || 0} tinnitus measurements
              </span>
            </div>
            <div className="flex items-center">
              <ChartBarIcon className="w-4 h-4 mr-2" />
              <span>
                {progressData && (progressData.questionnaire_results.length > 0 || progressData.tinnitus_profiles.length > 0)
                  ? 'Data available for analysis'
                  : 'No data available'
                }
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <ChartBarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Yet</h3>
          <p className="text-gray-600">
            Complete assessments and questionnaires to start tracking your progress.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProgressTracking;
